import { IPPrimaryButton, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { getProviderIcon } from 'components/Links/Helpers/LinkIcons';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import DocumentConnection from 'models/documentConnection';
import { DocumentProviderType, getProviderName } from 'models/documentProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { overflow } from 'utils/string';
import BrokeredFolderPicker from './BrokeredFolderPicker';

interface IProviderFolderChooserProps {
  connections: DocumentConnection[];
  connection?: DocumentConnection;
  folderName: string;
  /** The category is bound to its connection; shown, explained, but not changeable. */
  lockConnection?: boolean;
  selectConnection: (connection: DocumentConnection) => void;
  selectFolder: (containerId: string | undefined, folderId: string | undefined, name: string) => void;
}

/**
 * Picks the connection a category lives on and the folder inside it. That folder becomes the
 * category's root: browsing and uploading from the library stay inside it, because the
 * category permissions are the access control once a connection is shared tenant-wide.
 */
const ProviderFolderChooser = (props: IProviderFolderChooserProps) => {
  const { t } = useTranslation(['documentproviders', 'lists']);
  const [pickFolder, setPickFolder] = useState<boolean>(false);

  return (
    <IPStack tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPDropdown
            label={t('documentproviders:Category.ConnectionLabel')}
            disabled={props.lockConnection}
            selectedKey={props.connection?.documentConnectionId ?? null}
            options={props.connections.map((connection) => ({
              key: connection.documentConnectionId,
              text: `${connection.name} (${getProviderName(connection.providerType)})`,
            }))}
            onChange={(_ev, option) => {
              const connection = props.connections.find((c) => c.documentConnectionId === option?.key);
              if (connection) props.selectConnection(connection);
            }}
          />
          {props.lockConnection && <IPText variant="small">{t('documentproviders:Category.ConnectionLocked')}</IPText>}
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPPrimaryButton
          text={t('documentproviders:Picker.ChooseFolder')}
          iconProps={getProviderIcon(props.connection?.providerType ?? DocumentProviderType.SharePoint)}
          disabled={!props.connection}
          onClick={() => setPickFolder(true)}
        />
      </IPStack.Item>
      <IPStack.Item>
        {props.folderName ? (
          <IPText>{overflow(props.folderName, 80)}</IPText>
        ) : (
          <IPText>
            <i>{t('lists:AddNew.NoneSelected')}</i>
          </IPText>
        )}
      </IPStack.Item>
      {pickFolder && props.connection && (
        <BrokeredFolderPicker
          isOpen={pickFolder}
          connectionId={props.connection.documentConnectionId}
          close={() => setPickFolder(false)}
          onSelect={(containerId, folderId, path) => props.selectFolder(containerId, folderId, path)}
        />
      )}
    </IPStack>
  );
};

export default ProviderFolderChooser;
